import { VIDEO_FAMILIES, VIDEO_MODELS, getVideoModel, validateVideoRequest } from '../../../shared/video-config.js';

// These first-frame workflows are implemented by the installed browser SDK.
// Utility, reference-video, audio-guide, and last-frame-only workflows have
// different inputs and do not belong in the one-original-image Gallery action.
export function galleryVideoModels(snapshot) {
  const models = snapshot.data?.models || snapshot.models;
  return models.filter(model => model.mediaType === 'video' && VIDEO_MODELS[model.id])
    .map(model => ({ ...model, ...getVideoModel(model.id, model),
      workerCount: model.workerCounts.fast || 0 }));
}

export function videoSizeChoices(model) {
  if (model.partner) return model.parameters.resolutions.map(id => ({ id, label: id }));
  const levels = model.outputScale === 2 ? [768, 1088, 1536] : [768, 1024, 1536];
  return levels.map(size => ({ id: String(size), label: `Up to ${size}px short edge` }));
}

function nativeCanvas(model, source, outputShortEdge) {
  const params = model.parameters;
  const family = VIDEO_FAMILIES[model.family];
  const scale = model.outputScale || 1;
  const widthRange = params.width;
  const heightRange = params.height;
  const grid = Math.max(widthRange.step || family.dimensions.divisor, heightRange.step || family.dimensions.divisor);
  const aspect = source.width / source.height;
  const portrait = aspect < 1;
  const shortRange = portrait ? widthRange : heightRange;
  const desiredShort = Math.max(shortRange.min, Number(outputShortEdge) / scale);
  let best = null;
  const maxPixels = params.maxPixels || family.maxPixels || (model.family === 'wan22' ? 1048576 : Infinity);
  for (let short = Math.ceil(shortRange.min / grid) * grid; short <= Math.min(desiredShort, shortRange.max); short += grid) {
    const width = portrait ? short : Math.round(short * aspect / grid) * grid;
    const height = portrait ? Math.round(short / aspect / grid) * grid : short;
    if (width < widthRange.min || width > widthRange.max || height < heightRange.min || height > heightRange.max || width * height > maxPixels) continue;
    const difference = Math.abs(Math.log((width / height) / aspect));
    if (!best || difference < best.difference - 1e-9 || (Math.abs(difference - best.difference) < 1e-9 && width * height > best.width * best.height)) {
      best = { width, height, difference };
    }
  }
  if (!best || best.difference > 0.02) throw new Error('This model cannot preserve this original image’s shape at a supported size. Choose another model.');
  return { width: best.width, height: best.height };
}

// Partner canvases come from the server's named presets, not an invented grid.
// For first-frame generation the prepared original has exactly that shape, so
// HappyHorse's source-derived ratio agrees with the charged canvas as well.
function partnerCanvas(model, source, resolution, presets) {
  const choices = presets.filter(preset => {
    // SDK5.60.3 validates every non-H3 axis at >=480px, including partner presets.
    if (preset.width < 480 || preset.height < 480) return false;
    const label = preset.label.toLowerCase();
    return label.includes(resolution.toLowerCase());
  });
  const aspect = source.width / source.height;
  choices.sort((a, b) => Math.abs(Math.log((a.width / a.height) / aspect)) - Math.abs(Math.log((b.width / b.height) / aspect)));
  if (!choices.length) throw new Error('This resolution has no supported image-to-video size. Choose another resolution.');
  return { width: choices[0].width, height: choices[0].height, ratio: choices[0].ratio };
}

export function resolveGalleryVideoSettings(model, source, { resolution, durationSeconds, fps, generateAudio = true, network = 'fast', presets = [] }) {
  const canvas = model.partner ? partnerCanvas(model, source, resolution, presets) : nativeCanvas(model, source, resolution);
  const settings = validateVideoRequest({ modelId: model.id, catalogModel: model,
    ...canvas, durationSeconds, fps });
  const sourceAspect = source.width / source.height;
  const outputAspect = settings.width / settings.height;
  const paddingFraction = 1 - Math.min(sourceAspect / outputAspect, outputAspect / sourceAspect);
  return { ...settings, network, generateAudio: model.supportsNativeAudio && (model.nativeAudioAlwaysOn || generateAudio),
    modelParameters: model.parameters, ratio: canvas.ratio,
    outputWidth: settings.width * (model.outputScale || 1), outputHeight: settings.height * (model.outputScale || 1),
    sourceWidth: source.width, sourceHeight: source.height,
    paddingFraction, sourcePreserved: true };
}

export function videoSettingsToOptions(settings, prompt) {
  return { modelId: settings.modelId, prompt, width: settings.width, height: settings.height,
    fps: settings.fps, durationSeconds: settings.durationSeconds, frames: settings.frames,
    actualDurationSeconds: settings.timing.actualDurationSeconds, generateAudio: settings.generateAudio,
    network: settings.network, modelParameters: settings.modelParameters,
    outputWidth: settings.outputWidth, outputHeight: settings.outputHeight,
    sourceWidth: settings.sourceWidth, sourceHeight: settings.sourceHeight,
    ...(settings.ratio ? { ratio: settings.ratio } : {}) };
}
